import { useEffect, useContext } from "react"
import * as Yup from "yup"
import GenericAssetForm from "./common/GenericAssetForm"
import { useFormikContext } from "formik"

// Insurance initial values
export const insuranceInitialValues = {
  name: "",
  insuranceType: "",
  purchaseDate:"",
  providerName: "",
  policyNumber: "",
  policyHolderName: "",
  premiumAmount: "",
  premiumFrequency: "",
  maturityDate: "",
};

// Insurance validation schema
export const insuranceValidationSchema = Yup.object().shape({
  name: Yup.string().required("Policy name is required")
     .min(3, "Min length: 3 characters")
      .max(100, "Max length: 100 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      ),
    purchaseDate: Yup.string().required("Purchase Date is required"),
    purchasePrice: Yup.number()
      .typeError("Must be a valid number")
      .required("Sum Assured / Coverage Amount is required")
      .positive("Must be a positive number > 0")
      .min(0.01, "Must be greater than 0"),
  insuranceType: Yup.string().required("Insurance type is required"),
  providerName: Yup.string().required("Insurance Provider is required")
     .min(3, "Min length: 3 characters")
      .max(50, "Max length: 100 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      ),
  policyNumber: Yup.string()
    .required("Policy number is required")
    .min(3, "Min length: 3 characters")
    .max(50, "Max length: 50 characters")
    .matches(
      /^[A-Za-z0-9\-/]+$/,
      "Allowed: letters, numbers, -, /. No spaces or special characters."
    ),
  policyHolderName: Yup.string().required("Policy Holder Name is required")
      .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      ),
  premiumAmount: Yup.number()
    .typeError("Must be a valid number")
      .positive("Must be a positive number > 0")
      .min(0.01, "Must be greater than 0")
    // .required("Premium Amount is required")
    .when("subCategory", {
      is: (val) => val !== "important papers",
      then: (schema) => schema.positive("Must be a positive number > 0").min(0.01, "Must be greater than 0"),
      otherwise: (schema) => schema.notRequired(),
    }),
  // premiumFrequency: Yup.string().required("Premium frequency is required"),
  maturityDate: Yup.date()
    // .required("Policy expiry/maturity date is required")
    .when("purchaseDate", (purchaseDate, schema) => {
      if (!purchaseDate) return schema;
      const nextDay = new Date(purchaseDate);
      nextDay.setDate(nextDay.getDate() + 1);
      return schema.min(
        nextDay,
        "Maturity date must be after policy start date"
      );
    }),
});

/**
 * Insurance Form - Field-only component for AddAssets.js
 * This renders only the insurance-specific fields without form wrapper
 * Used in ADD mode within AddAssets.js's existing Formik wrapper
 */
const InsuranceForm = ({ values, setFieldValue, errors, touched, defaultCurrency }) => {
  const { setFieldTouched } = useFormikContext() || {}
  
  // Handle default currency setting
  useEffect(() => {
    if ((!values.purchaseCurrency || values.purchaseCurrency === "") && defaultCurrency) {
      setFieldValue("purchaseCurrency", defaultCurrency)
    }
  }, [values.purchaseCurrency, defaultCurrency, setFieldValue])

  return (
    <GenericAssetForm
      subCategory="insurance"
      values={values}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      errors={errors}
      touched={touched}
      defaultCurrency={defaultCurrency}
      readOnly={false}
    />
  )
}

export default InsuranceForm
